import { Classes, Dialog } from "@blueprintjs/core";
import { FieldGroup } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import {
  ExperienceProfessionnelleApi,
  ExperienceProfessionnelleFichierUploadDto,
  ExperienceProfessionnelleFichierUploadDtoFromJSON
} from "../../../../../../api";
import { FGWalterFileInput, FGWalterSelectInput, SmallFormGenerator } from "../../../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

const StyledDialog = styled(Dialog)`
  width: 600px;
  height: auto;
  background-color: white;
`;

export interface IExperienceProfessionnelleDocumentUploadDialogProps {
  idexperienceProfessionnelle: number;
  dialogOpen: boolean;
  onClose: (refresh?: boolean) => void;
}

export const ExperienceProfessionnelleDocumentUploadDialog: React.FunctionComponent<IExperienceProfessionnelleDocumentUploadDialogProps> = ({
  idexperienceProfessionnelle,
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(ExperienceProfessionnelleApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return ExperienceProfessionnelleFichierUploadDtoFromJSON({ idexperienceProfessionnelle });
        },
        saveApiFn: async (d: ExperienceProfessionnelleFichierUploadDto) => {
          return api.experienceProfessionnelleUploadExperienceProfessionnelleFichier({
            ExperienceProfessionnelleFichierUploadDto: d
          });
        },
        onSaved: () => onClose(true)
      }),
      [api, idexperienceProfessionnelle, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        idtypeAcquisFichier: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required)),
        file: Yup.object()
          .nullable()
          .shape({
            fileName: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required)),
            contentB64: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required))
          })
      }),
    [t]
  );

  const [typesAcquisFichier, tLoading] = useReferential(a => a.referentialGetTypesAcquisFichier());

  return (
    <StyledDialog title={t(ETLCodes.UploadFichier)} isOpen={dialogOpen} onClose={() => onClose(false)}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose(false)}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGWalterFileInput name="file" label={t(ETLCodes.Fichier)} requiredMark />
            <FGWalterSelectInput
              name="idtypeAcquisFichier"
              label={t(ETLCodes.Type)}
              items={typesAcquisFichier}
              loading={tLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
